import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Grid,
  Typography,
  Button,
  Box,
  TextField,
  MenuItem,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSystemNotificationDetailQuery } from '../../../types/generated-types';
import {
  useCreateSystemNotificationMutation,
  useUpdateSystemNotificationMutation,
} from '../../../types/generated-types';
import { useAuthorizer } from '../../auth/AuthorizationContext';
import { useInjectableComponents } from '../../system/services/injectableComponentsManager';
import { Delete } from '@mui/icons-material';
import { Actions } from '../../auth/types/ability';

// Entity type options
const ENTITY_TYPES = [
  { value: 'Organization', label: 'Organization' },
  { value: 'Property', label: 'Property' },
  { value: 'Unit', label: 'Unit' },
  { value: 'User', label: 'User' },
];

const validationSchema = Yup.object({
  entityId: Yup.string().required('Entity ID is required'),
  entityType: Yup.string().required('Entity type is required'),
  message: Yup.string().required('Message is required'),
  link: Yup.string().required('Link is required'),
  createdAt: Yup.number().required('Created date is required'),
  displayStart: Yup.number().required('Display start date is required'),
  displayEnd: Yup.number().required('Display end date is required'),
  acknowledgedAt: Yup.number().required('Acknowledged date is required'),
  requireAcknowledgement: Yup.boolean().required(
    'Require acknowledgement is required',
  ),
});

export const SystemNotificationEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const isEdit = !!id;
  const { setContextMenuItems, setTitle } = useInjectableComponents();

  const [createNotification] = useCreateSystemNotificationMutation();
  const [updateNotification] = useUpdateSystemNotificationMutation();

  const { data, loading, error } = useSystemNotificationDetailQuery({
    variables: { _id: id! },
    skip: !id,
  });

  const [selectedEntityType, setSelectedEntityType] = useState<string>('');

  // Set the page title
  useEffect(() => {
    setTitle(
      isEdit ? 'Edit System Notification' : 'Create System Notification',
    );
    return () => {
      setTitle(undefined);
    };
  }, [isEdit, setTitle]);

  // Set the context menu items
  useEffect(() => {
    if (isEdit) {
      const menuItems = [
        {
          id: 'delete-system-notification-menu-item',
          label: 'Delete System Notification',
          icon: <Delete fontSize="small" />,
          action: () => {
            // TODO: Implement delete functionality
            console.log('Delete system notification:', id);
          },
          permit: {
            action: 'delete' as Actions,
            subject: { __typename: 'SystemNotification', _id: id! },
          },
        },
      ];

      setContextMenuItems([
        {
          items: menuItems,
        },
      ]);
    } else {
      setContextMenuItems(undefined);
    }
    return () => {
      setContextMenuItems(undefined);
    };
  }, [isEdit, id, setContextMenuItems]);

  useEffect(() => {
    if (data?.systemNotificationById) {
      setSelectedEntityType(data.systemNotificationById.entityType);
    }
  }, [data]);

  if (loading && isEdit) {
    return (
      <div>
        <Typography variant="h6">Loading system notification...</Typography>
      </div>
    );
  }

  if (error && isEdit) {
    return (
      <div>
        <Typography variant="h6" color="error">
          Failed to load system notification
        </Typography>
      </div>
    );
  }

  const initialValues = {
    entityId: data?.systemNotificationById?.entityId || '',
    entityType: data?.systemNotificationById?.entityType || '',
    message: data?.systemNotificationById?.message || '',
    link: data?.systemNotificationById?.link || '',
    createdAt: data?.systemNotificationById?.createdAt || Date.now(),
    displayStart: data?.systemNotificationById?.displayStart || Date.now(),
    displayEnd:
      data?.systemNotificationById?.displayEnd || Date.now() + 86400000, // +24 hours
    acknowledgedAt: data?.systemNotificationById?.acknowledgedAt || 0,
    requireAcknowledgement:
      data?.systemNotificationById?.requireAcknowledgement || false,
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      if (isEdit) {
        await updateNotification({
          variables: {
            id: id!,
            input: values,
          },
        });
      } else {
        await createNotification({
          variables: {
            input: values,
          },
        });
      }
      navigate('/system-notifications');
    } catch (error) {
      console.error('Error saving system notification:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit System Notification' : 'Create System Notification'}
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Notification Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Entity Type"
                        select
                        value={selectedEntityType}
                        onChange={(e) => {
                          setSelectedEntityType(e.target.value);
                          setFieldValue('entityType', e.target.value);
                        }}
                        error={touched.entityType && !!errors.entityType}
                        helperText={touched.entityType && errors.entityType}
                        disabled={
                          !can('update', { __typename: 'SystemNotification' })
                        }
                      >
                        {ENTITY_TYPES.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Entity ID"
                        value={values.entityId}
                        onChange={(e) =>
                          setFieldValue('entityId', e.target.value)
                        }
                        error={touched.entityId && !!errors.entityId}
                        helperText={touched.entityId && errors.entityId}
                        disabled={
                          !can('update', { __typename: 'SystemNotification' })
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message"
                        multiline
                        rows={4}
                        value={values.message}
                        onChange={(e) =>
                          setFieldValue('message', e.target.value)
                        }
                        error={touched.message && !!errors.message}
                        helperText={touched.message && errors.message}
                        disabled={
                          !can('update', { __typename: 'SystemNotification' })
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Link"
                        value={values.link}
                        onChange={(e) => setFieldValue('link', e.target.value)}
                        error={touched.link && !!errors.link}
                        helperText={touched.link && errors.link}
                        disabled={
                          !can('update', { __typename: 'SystemNotification' })
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.requireAcknowledgement}
                            onChange={(e) =>
                              setFieldValue(
                                'requireAcknowledgement',
                                e.target.checked,
                              )
                            }
                            disabled={
                              !can('update', {
                                __typename: 'SystemNotification',
                              })
                            }
                          />
                        }
                        label="Require Acknowledgement"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Timing Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                          label="Display Start"
                          value={values.displayStart}
                          onChange={(newValue) =>
                            setFieldValue('displayStart', newValue)
                          }
                          disabled={
                            !can('update', { __typename: 'SystemNotification' })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error:
                                touched.displayStart && !!errors.displayStart,
                              helperText:
                                touched.displayStart && errors.displayStart,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                          label="Display End"
                          value={values.displayEnd}
                          onChange={(newValue) =>
                            setFieldValue('displayEnd', newValue)
                          }
                          disabled={
                            !can('update', { __typename: 'SystemNotification' })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: touched.displayEnd && !!errors.displayEnd,
                              helperText:
                                touched.displayEnd && errors.displayEnd,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                          label="Acknowledged At"
                          value={values.acknowledgedAt}
                          onChange={(newValue) =>
                            setFieldValue('acknowledgedAt', newValue)
                          }
                          disabled={
                            !can('update', { __typename: 'SystemNotification' })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error:
                                touched.acknowledgedAt &&
                                !!errors.acknowledgedAt,
                              helperText:
                                touched.acknowledgedAt && errors.acknowledgedAt,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>

            <Box display="flex" gap={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  isSubmitting ||
                  !can('update', { __typename: 'SystemNotification' })
                }
              >
                {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/system-notifications')}
              >
                Cancel
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </div>
  );
};
